import { useContext, useMemo } from 'react';
import { ConfigContext, AuthContext } from '@@components';
import ApiConfig, { IApiConfig } from '../../api/ApiConfig';

// Helper class to create the custom http object
class HttpProvider {
	private config: IApiConfig;
	private isRedirecting = false; // Prevent multiple simultaneous redirects

	constructor(config: IApiConfig) {
		this.config = config;
	}

	fetch = async (url: RequestInfo, init?: RequestInit): Promise<Response> => {
		// Ensure cookies are sent with the request
		const requestInit: RequestInit = {
			...init,
			credentials: 'include',
		};

		const response = await fetch(url, requestInit);

		// Check if the response is 401 Unauthorized
		if (response.status === 401 && !this.isRedirecting) {
			this.isRedirecting = true;

			const urlString = typeof url === 'string' ? url : url.toString();
			console.warn(`API call to ${urlString} returned 401 Unauthorized. Token may be expired. Redirecting to sign in.`);

			// Small delay to prevent overwhelming the auth system
			setTimeout(async () => {
				try {
					// Preserve current path when triggering sign-in
					const currentPath = window.location.pathname + window.location.search;
					await this.config.getAuthService().signIn(currentPath);
				} catch (error) {
					console.error('Error during sign-in redirect:', error);
				} finally {
					this.isRedirecting = false;
				}
			}, 100);

			// Return the original response so the calling code can handle it appropriately
			return response;
		}

		return response;
	};
}

function useApiClient<T>(
	Factory: new (config: IApiConfig, baseUrl: string, http?: { fetch(url: RequestInfo, init?: RequestInit): Promise<Response> }) => T
): T {
	const { API_URL } = useContext(ConfigContext);
	const auth = useContext(AuthContext);
	const client = useMemo(() => {
		const config = new ApiConfig(auth.authService);
		const httpProvider = new HttpProvider(config);
		// Pass the custom http object to the specific client
		return new Factory(config, API_URL, httpProvider);
	}, [Factory, auth.authService, API_URL]);
	return client as T;
}

export default useApiClient;
